// AuthCallbackPage.tsx
import { useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';

import { useUserRegistration } from '../../hooks/useUserRegistration';
import { useAuthStore } from '../../stores/auth.store';
import { redirectService } from '../redirect/redirectService';

export const AuthCallbackPage = () => {
  const {
    user: auth0User,
    isLoading: isAuth0Loading,
    getAccessTokenSilently,
  } = useAuth0();
  const navigate = useNavigate();

  const {
    checkUserExists,
    registerUser,
    isLoading: isRegistering,
  } = useUserRegistration();

  const [isHandlingCallback, setIsHandlingCallback] = useState(false);

  // Log component mount once
  useEffect(() => {
    console.log('[RedirectDebug] 🔄 AuthCallbackPage component mounted');
  }, []);

  const handleAuthCallback = useCallback(async () => {
    if (!auth0User || !auth0User.email || !auth0User.sub) {
      console.error('Auth0 user data is incomplete', auth0User);
      navigate('/login?error=auth0_data_missing', { replace: true });
      return;
    }

    if (!getAccessTokenSilently) {
      console.error('Auth0 getAccessTokenSilently not available');
      navigate('/login?error=auth0_token_missing', { replace: true });
      return;
    }

    try {
      // Set up the token getter in the auth store FIRST
      console.log('[RedirectDebug] Setting up Auth0 token...');
      const { setGetToken, setGetTokenSilently } = useAuthStore.getState();
      setGetToken(getAccessTokenSilently);
      setGetTokenSilently(getAccessTokenSilently);

      console.log(
        '[RedirectDebug] Auth0 token setup complete, checking user...'
      );
      const exists = await checkUserExists(auth0User.email, auth0User.sub);

      if (!exists) {
        console.log('[RedirectDebug] User does not exist, registering...');
        await registerUser({
          firstName: auth0User.given_name || '',
          lastName: auth0User.family_name || '',
        });
      } else {
        // If user exists, we still need to fetch their profile to populate the store
        // The registerUser mutation does this on success, but checkUser does not.
        console.log('[RedirectDebug] User exists, fetching profile...');
        const { fetchProfile } = useAuthStore.getState();
        await fetchProfile();
      }

      console.log('[RedirectDebug] User setup complete, executing redirect...');
      // Guard for double-callbacks: ensure we only redirect once
      if (sessionStorage.getItem('redirect_completed') === 'true') {
        console.log('[RedirectDebug] Redirect already completed, skipping.');
      } else {
        // On success, navigate to the intended page
        // First try redirect service, then fallback to sessionStorage
        console.log('[RedirectDebug] Executing redirect in callback...');
        const redirectSuccess = redirectService.executeRedirect(navigate);
        console.log('[RedirectDebug] Redirect success:', redirectSuccess);

        if (!redirectSuccess) {
          const destination =
            sessionStorage.getItem('auth_final_destination') || '/dashboard';
          console.log(
            '[RedirectDebug] Fallback to sessionStorage destination:',
            destination
          );
          navigate(destination, { replace: true });
        }

        // Mark redirect as completed to avoid subsequent executions
        sessionStorage.setItem('redirect_completed', 'true');
      }
    } catch (error) {
      console.error('Auth callback failed:', error);
      // On failure, redirect to login with an error
      navigate('/login?error=callback_failed', { replace: true });
    }
  }, [
    auth0User,
    getAccessTokenSilently,
    checkUserExists,
    registerUser,
    navigate,
  ]);

  useEffect(() => {
    // Wait for Auth0 to be ready
    if (!isHandlingCallback && !isAuth0Loading) {
      setIsHandlingCallback(true);
      handleAuthCallback().finally(() => {
        setIsHandlingCallback(false);
      });
    }
  }, [isAuth0Loading, handleAuthCallback, isHandlingCallback]);

  // Show loading state during any async operation
  if (isAuth0Loading || isRegistering || isHandlingCallback) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-xl font-semibold">
            Setting up your account...
          </div>
          <div className="text-gray-500">This may take a few moments</div>
        </div>
      </div>
    );
  }

  // If we're not loading and something went wrong, we'll have been redirected.
  // This component shouldn't render any other UI.
  return null;
};
